import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import dayjs from "dayjs";

import IconButton from "../../../../components/button/IconButton";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../../../../components/dropdown-menu/DropdownMenu";
import StatusBadge from "../../../../components/status-badge/StatusBadge";
import ToolTip from "../../../../components/tooltip/ToolTip";
import type {
  ProducerTask,
  TaskColumn,
  TaskCell as TaskCellData,
} from "../../../../app/services/taskBoard";
import { useMessengerSocket } from "../../../../app/messenger/MessengerSocketContext";
import { useTypingSender } from "../../../../hooks/useTypingSender";
import TaskCell from "./TaskCell";
import CheckbackBadge from "./CheckbackBadge";
import { TITLE_PRESENCE_COLUMN_ID } from "./useTaskBoardPresence";
import {
  extractDates,
  extractMentions,
  groupColumnsByParallel,
  isCellComplete,
  isTaskComplete,
  mentionVisibleCellContent,
  splitTaskByLines,
} from "./taskBoardUtils";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const TitleText = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 22px;
  min-height: 22px;
`;

const TITLE_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/** 縦積みセル: 並列グループ内の各工程セルを縦に並べる */
const StackedCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/**
 * 縦積みの 1 工程分。区切りは点線、完了は通常セルの `table-cell-dimmed` と同じ
 * 背景グレー + 淡文字で沈め、本日期限は内部の未チェックチェックボックスを青で
 * 強調する。行全体完了（table-row-muted）時は行側スタイルに委ねるため、
 * $dimmed は呼び出し側で false にする。
 * 左右 padding と負 margin は相殺され、$dimmed の背景だけがセル余白まで広がる。
 */
const StackedItem = styled.div<{ $dimmed: boolean; $dueToday: boolean }>`
  padding: 2px 6px;
  margin: 0 -6px;
  & + & {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px dashed var(--soremo-border);
  }
  ${({ $dimmed }) =>
    $dimmed
      ? `
    background-color: var(--soremo-border, #f0f0f0);
    color: var(--soremo-light-gray);
    border-radius: 4px;
  `
      : ""}
  ${({ $dueToday }) =>
    $dueToday
      ? "& .task-list-item-checkbox:not(:checked)::before { color: var(--soremo-blue); }"
      : ""}
`;

/**
 * 縦積み時の工程名ラベル（どの内容がどの工程かを示す）。
 * 書体は typography 系統（bodytext-11）に委ね、行間だけ utility で 150% に抑える
 * （既定の 200% では縦積みセルが間延びするため。GanttChart の日付目盛りと同じ扱い）。
 */
const StackedLabel = styled.div.attrs({
  className: "bodytext-11 u-text-light-gray u-line-height-150",
})`
  margin-bottom: 2px;
`;

/** タイトル列の「◯◯が編集中」バッジ（タイトルテキストの右に添える） */
const TitleEditingBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 8px;
  background: var(--soremo-blue);
  color: #fff;
  font-size: 10px;
  line-height: 1.5;
  white-space: nowrap;
`;

/* eslint-disable no-unused-vars -- コールバック props の引数名はドキュメント */
interface TaskBoardRowProps {
  task: ProducerTask;
  /** 貼り付けの上書きアンカー算出に使う表示順インデックス */
  rowIndex: number;
  /** セルへ貼り付けた画像のアップロードスコープになるプロジェクト ID */
  projectId: string;
  columns: TaskColumn[];
  /**
   * 表示する列（担当者フィルター中の列ごと非表示を適用済み）。
   * 完了判定・行分割などのロジックは `columns`（全列）で行い、
   * セルの描画だけをこちらに合わせる。未指定なら全列を表示する。
   */
  displayColumns?: TaskColumn[];
  editable: boolean;
  /** 並列工程を縦積み表示するか。false=従来の横並び */
  stackParallel?: boolean;
  /**
   * 担当者フィルター（本文の @表示名。複数 = OR）。選択中はセル内の表示も
   * 担当行だけに絞る（タスク名に担当者が書かれた行はタスク全体が担当範囲の
   * ため全文表示）。空なら絞り込みなし。
   */
  selectedMentions?: string[];
  /** タスク名と同名のショットがある場合の shot id（Preview へ遷移） */
  linkedShotId?: string;
  /**
   * 同名ショットがチェックバック（プロデューサー待ち）中の場合の shot id。
   * 指定時はタイトル横に「CB」タグを出し、ホバーで最新のクライアント
   * チェックバックコメントを表示する。
   */
  checkbackShotId?: string;
  onOpenShot: (shotId: string) => void;
  onPatchTitle: (taskId: string, title: string) => void;
  onChangeCell: (
    taskId: string,
    columnId: string,
    patch: Partial<TaskCellData>,
  ) => void;
  onRequestDelete: (task: ProducerTask) => void;
  /** セル内の改行ごとに行を分割する（確認モーダルは呼び出し側で出す） */
  onRequestSplit: (task: ProducerTask) => void;
  /** 行をコピーして 1 行追加する */
  onDuplicate: (task: ProducerTask) => void;
  /** 行を別ボードへ移動する（移動先選択モーダルは呼び出し側で出す） */
  onRequestMove: (task: ProducerTask) => void;
  /** 移動先にできるボードがあるか（無ければ「行を移動」を disabled にする） */
  canMove: boolean;
  /**
   * タスク名 × `@表示名` からオファーステータスのバッジラベルを解決する。
   * `null` ならバッジ非表示（スコープ不一致・完了済み等）。
   */
  resolveMentionStatus?: (
    taskTitle: string,
    mentionName: string,
  ) => string | null;
  /**
   * 指定セルを編集中の他ユーザーの表示名一覧を返す（編集中バッジ用）。
   * タイトル列は columnId = `TITLE_PRESENCE_COLUMN_ID` で照会する。
   */
  resolveCellEditors?: (taskId: string, columnId: string) => readonly string[];
  /**
   * `@表示名` → チェックボックス色（アクティビティ集計のユーザー配色）。
   * undefined の名前は既定色のまま。
   */
  mentionColorOf?: (name: string) => string | undefined;
}
/* eslint-enable no-unused-vars */

const TaskBoardRow: React.FC<TaskBoardRowProps> = ({
  task,
  rowIndex,
  projectId,
  columns,
  displayColumns = columns,
  editable,
  stackParallel = false,
  selectedMentions,
  linkedShotId,
  checkbackShotId,
  onOpenShot,
  onPatchTitle,
  onChangeCell,
  onRequestDelete,
  onRequestSplit,
  onDuplicate,
  onRequestMove,
  canMove,
  resolveMentionStatus,
  resolveCellEditors,
  mentionColorOf,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);

  // タイトル列の編集中 presence 送信（セルと同じ経路。列は予約値 __title）。
  const { sendEvent } = useMessengerSocket();
  const { onTypingChange: onTitleTypingChange, onTypingCommit: onTitleTypingCommit } =
    useTypingSender({
      send: sendEvent,
      scope: "task_board",
      ids: {
        project_id: projectId,
        task_id: task.id,
        column_id: TITLE_PRESENCE_COLUMN_ID,
      },
      enabled: editable && !!projectId,
    });

  const commitTitle = () => {
    setIsEditingTitle(false);
    onTitleTypingCommit();
    const trimmed = titleDraft.trim();
    if (trimmed === task.title) return;
    onPatchTitle(task.id, trimmed);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setTitleDraft(task.title);
      setIsEditingTitle(false);
      onTitleTypingCommit();
    }
  };

  // ショットリスト完了行と同じ視覚（ダークグレー背景 + 文字白反転）を、
  // 「行内のチェックボックスを持つ全セルが完了」のときに行全体へ適用する。
  const rowCompleted = isTaskComplete(task, columns);

  // 縦積み表示用の並列グループ（直列境界で分割）。横並び時は使わない。
  // 担当者フィルター中は列ごと非表示を適用済みの displayColumns から組む。
  const columnGroups = useMemo(
    () => (stackParallel ? groupColumnsByParallel(displayColumns) : []),
    [stackParallel, displayColumns],
  );

  // 担当者フィルター中、セル内の表示を担当行だけに絞るためのメンション。
  // タスク名に選択中の担当者が書かれている行は、タスク全体が担当範囲なので
  // 絞らない（null = 全文表示）。縦積み・横並びの両方で同じ扱い。
  const mentionFilterActive = !!selectedMentions && selectedMentions.length > 0;
  const visibleLineMentions = useMemo<string[] | null>(() => {
    if (!selectedMentions || selectedMentions.length === 0) return null;
    const titleMentions = extractMentions(task.title || "");
    if (titleMentions.some((name) => selectedMentions.includes(name))) {
      return null;
    }
    return selectedMentions;
  }, [selectedMentions, task.title]);

  // 担当者フィルター中、このセルに表示する内容が無い（元から空か、担当行の
  // 絞り込みで全行落ちた）ときはセルごと非表示にする（編集導線も出さない）。
  const cellHiddenByMentions = (content: string): boolean =>
    mentionFilterActive &&
    !mentionVisibleCellContent(content, visibleLineMentions).trim();

  // 行操作メニュー。分割はセル内に改行があるときだけ、移動は移動先の
  // ボードがあるときだけ選べる。
  const rowActions = useMemo<DropdownMenuItem[]>(() => {
    const splitCount = splitTaskByLines(task, columns).length;
    return [
      {
        label: "行を分割",
        icon: "call_split",
        disabled: splitCount === 0,
        onClick: () => onRequestSplit(task),
      },
      {
        label: "行を複製",
        icon: "content_copy",
        onClick: () => onDuplicate(task),
      },
      {
        label: "行を移動",
        icon: "drive_file_move",
        disabled: !canMove,
        onClick: () => onRequestMove(task),
      },
      {
        label: "行を削除",
        icon: "delete",
        onClick: () => onRequestDelete(task),
      },
    ];
  }, [
    task,
    columns,
    canMove,
    onRequestSplit,
    onDuplicate,
    onRequestMove,
    onRequestDelete,
  ]);

  // 「本日日付」の青強調は行ではなく**セル単位**で適用する。各セル本文に
  // 今日の日付が書かれている場合のみ、そのセル内の未チェックチェックボックスを
  // 目立つ青へ切り替える（CSS は Table 側で定義）。
  const today = dayjs().startOf("day");
  const cellHasToday = (content: string): boolean => {
    const dates = extractDates(content, today);
    return dates.some((d) => d.isSame(today, "day"));
  };

  // `@表示名` の直後に、対応するオファーのステータスバッジを差し込む。
  // スコープ（タスク名 × オファー scope）一致 + 表示名の部分一致のときのみ表示。
  const renderMentionSuffix = useCallback(
    (name: string): React.ReactNode => {
      const label = resolveMentionStatus?.(task.title, name);
      if (!label) return null;
      // chip variant（枠線 + 背景）で本文テキストとの区別を付ける
      return (
        <StatusBadge
          variant="chip"
          color="border"
          size="small"
          label={label}
          className="u-ml8"
        />
      );
    },
    [resolveMentionStatus, task.title],
  );

  // 1 セル（td）の描画。横並びと、縦積みの単一列グループで共用する。
  const renderCell = (col: TaskColumn, serialBoundary: boolean) => {
    const cell = task.cells?.[col.id];
    const content = cell?.content ?? "";
    // 担当者フィルター中で表示する内容が無いセルは td を空にする
    // （直列境界線だけ残して表の構造は保つ）。
    if (cellHiddenByMentions(content)) {
      return (
        <td
          key={col.id}
          className={serialBoundary ? "task-col-serial" : undefined}
        />
      );
    }
    const complete = cell ? isCellComplete(content) : false;
    const dueToday = !complete && cellHasToday(content);
    const tdClass = [
      complete ? "table-cell-dimmed" : "",
      serialBoundary ? "task-col-serial" : "",
      dueToday ? "task-cell-due-today" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      // height: 1px は「高さ指定のある td」にするためのハック（実高さは内容で
      // 決まるので 1px には縮まない）。td に高さ指定がないと、TaskCell が編集中
      // に使う height: 100%（テキストエリアをセルいっぱいに広げて上端から
      // 始める）が解決されず、他セル由来で行が高いときに中央へ浮いてしまう。
      <td
        key={col.id}
        className={tdClass || undefined}
        style={{ height: "1px" }}
      >
        <TaskCell
          cell={cell}
          editable={editable}
          projectId={projectId}
          taskId={task.id}
          columnId={col.id}
          remoteEditors={resolveCellEditors?.(task.id, col.id)}
          onChangeContent={(c) => onChangeCell(task.id, col.id, { content: c })}
          visibleLineMentions={visibleLineMentions}
          renderMentionSuffix={renderMentionSuffix}
          checkboxColorOf={mentionColorOf}
        />
      </td>
    );
  };

  // 縦積みモードの 1 グループ分の描画。並列グループ（複数列）はセルを縦に積み、
  // 単一列グループ（直列）は通常セル（renderCell）と同じ見た目にする。
  const renderGroupCell = (group: TaskColumn[], serialBoundary: boolean) => {
    if (group.length === 1) {
      return renderCell(group[0], serialBoundary);
    }
    // 担当者フィルター中は、表示する内容が無い工程を段ごと畳む
    // （工程名ラベルだけが並ぶノイズを避ける）。全段落ちたら td を空にする。
    const visibleGroup = group.filter(
      (col) => !cellHiddenByMentions(task.cells?.[col.id]?.content ?? ""),
    );
    if (visibleGroup.length === 0) {
      return (
        <td
          key={`g-${group[0].id}`}
          className={serialBoundary ? "task-col-serial" : undefined}
        />
      );
    }
    // グループ内の全セルが完了なら、通常表示と同じく td 全体をグレーアウトする
    // （横並び表示の table-cell-dimmed と見た目を揃える）。
    const groupComplete = visibleGroup.every((col) =>
      isCellComplete(task.cells?.[col.id]?.content ?? ""),
    );
    const tdClass = [
      groupComplete ? "table-cell-dimmed" : "",
      serialBoundary ? "task-col-serial" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <td key={`g-${group[0].id}`} className={tdClass || undefined}>
        <StackedCell>
          {visibleGroup.map((col) => {
            const cell = task.cells?.[col.id];
            const content = cell?.content ?? "";
            const complete = cell ? isCellComplete(content) : false;
            // 行全体完了時は行側スタイル、グループ全体完了時は td 側スタイルに
            // 委ね、個別の淡色化はグループ内の一部完了時のみ適用する
            const dimmed = complete && !rowCompleted && !groupComplete;
            const dueToday = !complete && cellHasToday(content);
            return (
              <StackedItem key={col.id} $dimmed={dimmed} $dueToday={dueToday}>
                <StackedLabel>{col.title}</StackedLabel>
                <TaskCell
                  cell={cell}
                  editable={editable}
                  projectId={projectId}
                  taskId={task.id}
                  columnId={col.id}
                  remoteEditors={resolveCellEditors?.(task.id, col.id)}
                  onChangeContent={(c) =>
                    onChangeCell(task.id, col.id, { content: c })
                  }
                  visibleLineMentions={visibleLineMentions}
                  renderMentionSuffix={renderMentionSuffix}
                  checkboxColorOf={mentionColorOf}
                />
              </StackedItem>
            );
          })}
        </StackedCell>
      </td>
    );
  };

  // タイトル列を編集中の他ユーザー（presence は列予約値 __title で照会）
  const titleEditors =
    resolveCellEditors?.(task.id, TITLE_PRESENCE_COLUMN_ID) ?? [];
  const titleEditingBadge =
    titleEditors.length > 0 ? (
      <TitleEditingBadge aria-label={`${titleEditors.join("、")}さんが編集中`}>
        {titleEditors.join("、")}が編集中
      </TitleEditingBadge>
    ) : null;

  return (
    <tr
      data-row-index={rowIndex}
      className={rowCompleted ? "table-row-muted" : undefined}
    >
      {/* タスク名（同名ショットがあれば Preview へのリンク） */}
      <td>
        {isEditingTitle ? (
          <input
            type="text"
            autoFocus
            value={titleDraft}
            onChange={(e) => {
              setTitleDraft(e.target.value);
              onTitleTypingChange();
            }}
            onBlur={commitTitle}
            onKeyDown={handleTitleKeyDown}
            style={TITLE_INPUT_STYLE}
          />
        ) : linkedShotId ? (
          // ShotTable と同じく、行頭にプレビュー遷移用の IconButton（open_in_new）
          // を置き、タスク名は通常テキストで表示する。色変更（青）は行わない。
          <TitleRow>
            <IconButton
              icon="open_in_new"
              size={16}
              title={`「${task.title}」のプレビューを開く`}
              ariaLabel="プレビューを開く"
              onClick={() => onOpenShot(linkedShotId)}
            />
            <ToolTip description={task.title} truncatedOnly>
              <TitleText
                className="bodytext-13"
                onClick={() => {
                  if (!editable) return;
                  setTitleDraft(task.title);
                  setIsEditingTitle(true);
                }}
                style={{ cursor: editable ? "text" : "default" }}
              >
                {task.title}
              </TitleText>
            </ToolTip>
            {titleEditingBadge}
            {checkbackShotId && <CheckbackBadge shotId={checkbackShotId} />}
          </TitleRow>
        ) : (
          <TitleRow>
            <ToolTip description={task.title} truncatedOnly>
              <div
                className="bodytext-13"
                onClick={() => {
                  if (!editable) return;
                  setTitleDraft(task.title);
                  setIsEditingTitle(true);
                }}
                style={{
                  flex: 1,
                  minHeight: 22,
                  lineHeight: "22px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: editable ? "text" : "default",
                }}
              >
                {task.title || (editable ? "（無題）" : "")}
              </div>
            </ToolTip>
            {titleEditingBadge}
          </TitleRow>
        )}
      </td>

      {/* 各列のセル（縦積みモードでは並列グループを 1 セルに縦積み） */}
      {stackParallel
        ? columnGroups.map((group, groupIndex) =>
            // 先頭グループ以外は必ず直列列で分割される＝直列境界になる
            renderGroupCell(group, groupIndex > 0),
          )
        : displayColumns.map((col, columnIndex) =>
            renderCell(col, columnIndex > 0 && !col.parallel),
          )}

      {/* 行操作（分割・複製・削除を 3 点リーダーに畳む） */}
      <td>
        {editable && (
          <div className="u-row-center">
            <DropdownMenu items={rowActions} size="small" />
          </div>
        )}
      </td>
    </tr>
  );
};

export default TaskBoardRow;
